import { portalText, usePortalTranslation } from "../i18n/portal.js";
/**
 * What the console shows when the sign-in itself cannot be read (customer-portal review
 * 2026-09-25 #150).
 *
 * The shell's guard reads the auth_session (`GET /v1/session`). When that read failed, nothing
 * caught the error and the framework's own "Something went wrong! Show Error" replaced the whole
 * console. Since 2026-10-06 the workspace listing is no longer part of that read: a failed listing
 * leaves the shell up and is shown in its sidebar card and switcher (`current-workspace.ts`), so
 * this page is for the sign-in read itself failing. This says what failed in plain
 * words, with the request id when WeTalk itself failed, and offers Try again — which runs the
 * guard again rather than reloading the page, so a passing blip costs one click. A page below the
 * shell that fails without its own error handling lands here too, which is still better than the
 * framework's page: it says what failed.
 */
import { useQueryClient } from "@tanstack/react-query";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@wetalk/ui";
import type { ReactNode } from "react";

import { apiProblemText } from "../api/api-error.js";
import { QUERY_KEY } from "../api/query.js";

export function ShellFailure({ error }: ErrorComponentProps): ReactNode {
  usePortalTranslation("shell");
  const router = useRouter();
  const queryClient = useQueryClient();
  return (
    <section className="c-page" role="alert">
      <h1>{portalText("shell", "this_page_could_not_load_f25d91d4")}</h1>
      <p className="c-note c-note-critical">{apiProblemText(error, portalText("shell", "something_on_it_failed_e9ecbbc2"))}</p>
      <Button
        variant="primary"
        onClick={() => {
          void queryClient.resetQueries({ queryKey: QUERY_KEY.authSession }).then(() => router.invalidate());
        }}
      >
        {portalText("shell", "try_again_042c862e") + " "}</Button>
    </section>
  );
}
